import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Download, Trash, FileText, CheckCircle, Clock } from 'lucide-react';
import { message, Spin, Card, Descriptions, Table, Tag, Button, Modal, Row, Col, Divider, Timeline, Image } from 'antd';
import {
    UserOutlined,
    EnvironmentOutlined,
    BarsOutlined,
    HistoryOutlined,
    ThunderboltOutlined,
    FileTextOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
    fetchQuotations,
    deleteQuotation,
    updateQuotationStatus,
    downloadQuotationPDF,
    createQuotationVersion,
    addInvoice,
    fetchConfig,
    fetchClients,
    updateInvoiceNumber,
    getQuotationVersions
} from '../utils/api';
import { formatInvoiceNumber } from '../utils/invoiceNumber';
import { isViewer } from '../utils/auth';
import { numberToWords } from '../utils/numberToWords';
import EditQuote from './EditQuote';

// Helper to format currency
const formatIndianCurrency = (num) => {
    const n = Number(num);
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 2
    }).format(n);
};



function QuoteDetails({ currentUser }) {
    const { id } = useParams();
    const navigate = useNavigate();
    const [quote, setQuote] = useState(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);
    const [config, setConfig] = useState({});
    const [clients, setClients] = useState([]);
    const [versions, setVersions] = useState([]);

    // Edit state
    const [showEditModal, setShowEditModal] = useState(false);

    const loadData = async () => {
        try {
            setLoading(true);
            const [quotesData, configData, clientsData, versionsData] = await Promise.all([
                fetchQuotations(),
                fetchConfig(),
                fetchClients(),
                getQuotationVersions(id).catch(() => []) // Handle error gracefully if versions fail or strict 404
            ]);

            const foundQuote = quotesData.find(q => q._id === id || q.id === id);
            if (foundQuote) {
                setQuote(foundQuote);
            } else {
                message.error('Quotation not found');
            }
            setConfig(configData || {});
            setClients(clientsData || []);
            setVersions(versionsData || []);
        } catch (error) {
            console.error('Error loading quote details:', error);
            message.error('Failed to load quote details');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            loadData();
        }
    }, [id]);

    const handleDownloadPDF = async () => {
        if (downloading) return;
        try {
            setDownloading(true);
            const blob = await downloadQuotationPDF(quote._id || quote.id);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const clientName = (quote.clientName || quote.client?.name || 'Client').replace(/[^a-zA-Z0-9]/g, '_');
            const filename = `Quote_${quote.quoteNumber}_${clientName}.pdf`;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (error) {
            console.error('PDF Download Error:', error);
            message.error('Failed to download PDF');
        } finally {
            setDownloading(false);
        }
    };

    const handleDelete = () => {
        Modal.confirm({
            title: 'Delete Quotation',
            content: 'Are you sure you want to delete this quotation? This action cannot be undone.',
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    await deleteQuotation(quote._id);
                    message.success('Quotation deleted successfully');
                    navigate('/quote-history');
                } catch (error) {
                    message.error('Failed to delete quotation');
                }
            }
        });
    };

    const handleEditClick = () => {
        setShowEditModal(true);
    };

    const handleEditCancel = () => {
        setShowEditModal(false);
    };

    const handleEditSave = async (data) => {
        try {
            const newVersion = await createQuotationVersion(quote._id, data);
            setQuote(newVersion);
            setShowEditModal(false);
            message.success('Quotation updated successfully (new version created)');
            loadData(); // Refresh to ensure everything is synced
        } catch (error) {
            console.error('Edit error:', error);
            message.error('Failed to update quotation: ' + error.message);
        }
    };

    // Invoice creation state
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [invoiceData, setInvoiceData] = useState(null);
    const [creatingInvoice, setCreatingInvoice] = useState(false);

    const handleCreateInvoice = async () => {
        if (!quote) return;

        try {
            // Calculate financial year
            const currentDate = new Date();
            const currentYear = currentDate.getFullYear();
            const nextYear = currentYear + 1;
            const currentFinancialYear = `${currentYear}-${nextYear.toString().slice(-2)}`;

            // Generate invoice number
            const newInvoiceNumber = formatInvoiceNumber(config?.nextInvoiceNumber || 1, currentFinancialYear, config);

            const bankAccount = config?.bankAccounts && config.bankAccounts.length > 0
                ? config.bankAccounts[0]
                : null;

            const items = quote.items.map(item => {
                const unitPrice = Number(item.rate || 0);
                const qty = Number(item.qty || 1);
                const netAmount = Number(item.baseAmount || unitPrice * qty);
                const taxPercent = Number(item.taxPercent || 0);
                const taxAmount = Number(item.taxAmount || netAmount * (taxPercent / 100));
                const totalAmount = Number(item.totalAmount || netAmount + taxAmount);

                return {
                    hsn: item.hsn || '',
                    description: item.description || '',
                    qty: qty,
                    unitPrice: unitPrice,
                    uom: item.uom || '',
                    netAmount: netAmount,
                    tax: taxPercent,
                    taxAmount: taxAmount,
                    totalAmount: totalAmount,
                    IsServc: 'N' // Default to N, or logic if needed
                };
            });

            // Calculate totals
            const quoteDiscount = Number(quote.totals?.discount || 0);
            const quoteTax = Number(quote.totals?.tax || 0);
            const quoteTotal = Number(quote.totals?.total || 0);
            const subtotal = Number(quote.totals?.subtotal || items.reduce((sum, item) => sum + item.netAmount, 0));
            const totalTax = quoteTax || items.reduce((sum, item) => sum + item.taxAmount, 0);
            const total = quoteTotal || (subtotal - quoteDiscount + totalTax);

            const preparedInvoiceData = {
                invoiceNumber: newInvoiceNumber,
                invoiceDate: dayjs().format('YYYY-MM-DD'),
                financialYear: currentFinancialYear,
                client: quote.client?._id,
                clientName: quote.clientName || quote.client?.name || '',
                billToAddress: quote.client?.billToAddress || '',
                shipToAddress: quote.client?.shipToAddress || '',
                clientContact: quote.client?.contact || '',
                items: items,
                totals: {
                    subtotal: Number(subtotal.toFixed(2)),
                    discount: quoteDiscount,
                    cgst: Number((totalTax / 2).toFixed(2)),
                    sgst: Number((totalTax / 2).toFixed(2)),
                    roundOff: Number((quote.totals?.roundOff || 0).toFixed(2)),
                    total: Math.round(total)
                },
                amountInWords: numberToWords(Math.round(total)),
                reference: quote.quoteNumber,
                relatedQuotation: quote._id,
                invoiceType: 'adhoc',
                creator: currentUser?.id || currentUser?._id,
                createdBy: currentUser?.name || 'Unknown User',
                bankAccount: bankAccount
            };

            setInvoiceData(preparedInvoiceData);
            setShowInvoiceModal(true);
        } catch (error) {
            console.error('Error preparing invoice:', error);
            message.error('Failed to prepare invoice: ' + error.message);
        }
    };

    const confirmCreateInvoice = async () => {
        if (!invoiceData) return;
        setCreatingInvoice(true);
        try {
            const createdInvoice = await addInvoice(invoiceData);
            await updateQuotationStatus(quote._id, 'Invoiced');

            // Increment invoice number
            await updateInvoiceNumber((config?.nextInvoiceNumber || 1) + 1);

            message.success('Invoice created successfully!');
            navigate(`/invoice/${createdInvoice._id}`);
        } catch (error) {
            console.error('Error creating invoice:', error);
            message.error('Failed to create invoice: ' + error.message);
        } finally {
            setCreatingInvoice(false);
            setShowInvoiceModal(false);
        }
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!quote) return <div>Quotation not found</div>;

    const getStatusColor = (status) => {
        switch (status) {
            case 'Created': return 'default';
            case 'Approved': return 'success';
            case 'Invoiced': return 'processing';
            default: return 'default';
        }
    };

    const handleApprove = () => {
        Modal.confirm({
            title: 'Approve Quotation',
            content: 'Are you sure you want to approve this quotation? This will lock the quotation and allow invoice creation.',
            okText: 'Approve',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    await updateQuotationStatus(quote._id, 'Approved');
                    message.success('Quotation approved successfully');
                    loadData();
                } catch (error) {
                    console.error('Error approving quotation:', error);
                    message.error('Failed to approve quotation: ' + error.message);
                }
            }
        });
    };

    return (
        <div className="container invoice-details-container">
            <button
                className="btn invoice-back-btn"
                onClick={() => navigate('/quote-history')}
            >
                <ArrowLeft size={16} /> Back to History
            </button>

            <Row gutter={[24, 24]}>
                {/* LEFT COLUMN: QUOTE DATA VIEW */}
                <Col xs={24} lg={18} xl={19}>
                    {/* Header Info */}
                    <Card title={
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>
                                <FileTextOutlined style={{ marginRight: 8 }} />
                                Quotation #{quote.quoteNumber} (v{quote.version})
                            </span>
                            <Tag color={getStatusColor(quote.status)}>
                                {quote.status || 'Created'}
                            </Tag>
                        </div>
                    } style={{ marginBottom: '20px' }}>
                        <Descriptions column={{ xs: 1, sm: 2, md: 2 }} size="small">
                            <Descriptions.Item label="Quote Date">
                                {quote.quoteDate ? dayjs(quote.quoteDate).format('DD MMM YYYY') : 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Valid Until">
                                {quote.validUntil ? dayjs(quote.validUntil).format('DD MMM YYYY') : 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Reference">{quote.reference || 'N/A'}</Descriptions.Item>
                            <Descriptions.Item label="Place of Supply">{quote.placeOfSupply || 'N/A'}</Descriptions.Item>
                            <Descriptions.Item label="Created By">{quote.creator?.name || 'N/A'}</Descriptions.Item>
                        </Descriptions>
                    </Card>

                    {/* Bill To & Ship To */}
                    <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', flexWrap: 'wrap' }}>
                        <Card title={<span><UserOutlined style={{ marginRight: 8 }} /> Client Details</span>} style={{ flex: 1, minWidth: '280px' }}>
                            <Descriptions column={1} size="small">
                                <Descriptions.Item label="Name">
                                    <strong>{quote.clientName || quote.client?.name || 'N/A'}</strong>
                                </Descriptions.Item>
                                <Descriptions.Item label="Address">
                                    {quote.billToAddress || quote.client?.billToAddress || quote.client?.address || 'N/A'}
                                </Descriptions.Item>
                                <Descriptions.Item label="GSTIN">{quote.client?.gstin || 'N/A'}</Descriptions.Item>
                            </Descriptions>
                        </Card>
                    </div>

                    {/* Line Items */}
                    <Card title={<span><BarsOutlined style={{ marginRight: 8 }} /> Line Items</span>} style={{ marginBottom: '20px' }}>
                        <Table
                            dataSource={quote.items?.map((item, idx) => ({ ...item, key: idx }))}
                            pagination={false}
                            size="small"
                            scroll={{ x: 'max-content' }}
                            columns={[
                                { title: '#', dataIndex: 'key', width: 50, render: (_, __, idx) => idx + 1 },
                                {
                                    title: 'Image',
                                    dataIndex: 'imageUrl',
                                    key: 'imageUrl',
                                    width: 80,
                                    render: (url) => {
                                        if (!url) return null;
                                        const baseUrl = window.location.hostname === 'ledgerly.spatium360.com'
                                            ? 'https://ledgerly.spatium360.com'
                                            : 'http://localhost:5050';
                                        return (
                                            <Image
                                                src={`${baseUrl}${url}`}
                                                width={50}
                                                height={50}
                                                style={{ objectFit: 'cover', borderRadius: '4px' }}
                                            />
                                        );
                                    }
                                },
                                { title: 'Description', dataIndex: 'description', key: 'description' },
                                { title: 'Qty', dataIndex: 'qty', key: 'qty', width: 80, align: 'right' },
                                { title: 'Rate', dataIndex: 'rate', key: 'rate', width: 120, align: 'right', render: v => formatIndianCurrency(v || 0) },
                                { title: 'GST %', dataIndex: 'taxPercent', key: 'taxPercent', width: 80, align: 'right', render: v => `${v}%` },
                                { title: 'Amount', dataIndex: 'totalAmount', key: 'totalAmount', width: 120, align: 'right', render: v => formatIndianCurrency(v || 0) },
                            ]}
                        />

                        {/* Totals */}
                        <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                            <div style={{ width: '280px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                                    <span style={{ color: '#666' }}>Subtotal:</span>
                                    <span>{formatIndianCurrency(quote.totals?.subtotal || 0)}</span>
                                </div>
                                {Number(quote.totals?.discount || 0) > 0 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f0f0f0', color: '#16a34a' }}>
                                        <span>Discount{quote.totals?.discountType === 'percentage' && quote.totals?.discountRate ? ` (${quote.totals.discountRate}%)` : ''}:</span>
                                        <span>-{formatIndianCurrency(quote.totals.discount)}</span>
                                    </div>
                                )}
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                                    <span style={{ color: '#666' }}>SGST:</span>
                                    <span>{formatIndianCurrency(quote.totals?.sgst || (quote.totals?.tax ? quote.totals.tax / 2 : 0))}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                                    <span style={{ color: '#666' }}>CGST:</span>
                                    <span>{formatIndianCurrency(quote.totals?.cgst || (quote.totals?.tax ? quote.totals.tax / 2 : 0))}</span>
                                </div>
                                {quote.totals?.roundOff !== undefined && quote.totals?.roundOff !== 0 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                                        <span style={{ color: '#666' }}>Round Off:</span>
                                        <span>{formatIndianCurrency(quote.totals.roundOff)}</span>
                                    </div>
                                )}
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', fontWeight: 'bold', fontSize: '16px', borderTop: '2px solid #1890ff' }}>
                                    <span>Total:</span>
                                    <span style={{ color: '#1890ff' }}>{formatIndianCurrency(quote.totals?.total || 0)}</span>
                                </div>
                            </div>
                        </div>
                    </Card>

                    {/* Version History */}
                    {versions.length > 0 && (
                        <Card title={<span><HistoryOutlined style={{ marginRight: 8 }} /> Version History</span>} style={{ marginBottom: '20px' }}>
                            <Table
                                dataSource={versions}
                                rowKey="_id"
                                pagination={false}
                                size="small"
                                columns={[
                                    {
                                        title: 'Version',
                                        dataIndex: 'version',
                                        key: 'version',
                                        render: (v, r) => (
                                            <Tag color={r._id === quote._id ? 'blue' : 'default'}>
                                                v{v} {r._id === quote._id && '(Current)'}
                                            </Tag>
                                        )
                                    },
                                    {
                                        title: 'Date',
                                        dataIndex: 'quoteDate',
                                        key: 'quoteDate',
                                        render: (d) => d ? dayjs(d).format('DD MMM YYYY, HH:mm') : '-'
                                    },
                                    {
                                        title: 'Amount',
                                        dataIndex: ['totals', 'total'],
                                        key: 'amount',
                                        render: (v) => formatIndianCurrency(v || 0)
                                    },
                                    {
                                        title: 'Created By',
                                        dataIndex: ['creator', 'name'],
                                        key: 'creator',
                                        render: (text) => text || 'N/A'
                                    },
                                    {
                                        title: 'Action',
                                        key: 'action',
                                        render: (_, record) => (
                                            <Button
                                                type="link"
                                                size="small"
                                                onClick={() => navigate(`/quotes/${record._id}`)}
                                                disabled={record._id === quote._id}
                                            >
                                                View
                                            </Button>
                                        )
                                    }
                                ]}
                            />
                        </Card>
                    )}
                </Col>

                {/* RIGHT COLUMN: ACTIONS */}
                <Col xs={24} lg={6} xl={5}>
                    <Card title={<span><ThunderboltOutlined style={{ marginRight: 8 }} /> Actions</span>}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {!isViewer() && (
                                <>
                                    <Button
                                        icon={<Edit size={16} />}
                                        onClick={handleEditClick}
                                        disabled={quote.status === 'Invoiced'}
                                        block
                                    >
                                        Edit Quote
                                    </Button>

                                    {quote.status === 'Created' && (
                                        <Button
                                            icon={<CheckCircle size={16} />}
                                            onClick={handleApprove}
                                            block
                                            style={{ borderColor: '#52c41a', color: '#52c41a' }}
                                        >
                                            Approve Quote
                                        </Button>
                                    )}
                                </>
                            )}

                            <Button
                                icon={downloading ? <Spin size="small" /> : <Download size={16} />}
                                onClick={handleDownloadPDF}
                                disabled={downloading}
                                loading={downloading}
                                block
                            >
                                Download PDF
                            </Button>

                            {!isViewer() && quote.status !== 'Invoiced' && (
                                <Button
                                    type="primary"
                                    icon={<FileText size={16} />}
                                    onClick={handleCreateInvoice}
                                    block
                                >
                                    Create Invoice
                                </Button>
                            )}

                            {!isViewer() && (
                                <>
                                    <Divider style={{ margin: '8px 0' }} />

                                    <Button
                                        icon={<Trash size={16} />}
                                        onClick={handleDelete}
                                        block
                                        danger
                                    >
                                        Delete Quote
                                    </Button>
                                </>
                            )}
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Edit Modal */}
            {showEditModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    zIndex: 1000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        width: '95%',
                        maxWidth: '1200px',
                        maxHeight: '90vh',
                        overflow: 'auto',
                        padding: '20px'
                    }}>
                        <EditQuote
                            quotation={quote}
                            clients={clients}
                            config={config}
                            user={currentUser}
                            onSave={handleEditSave}
                            onCancel={handleEditCancel}
                        />
                    </div>
                </div>
            )}

            {/* Confirmation Modal for Creating Invoice */}
            <Modal
                title="Confirm Invoice Creation"
                open={showInvoiceModal}
                onCancel={() => setShowInvoiceModal(false)}
                onOk={confirmCreateInvoice}
                confirmLoading={creatingInvoice}
                okText="Confirm & Create"
                width={600}
            >
                {invoiceData && (
                    <div>
                        <p>You are about to create an invoice from this quotation. Please verify the details below:</p>
                        <Descriptions column={1} bordered size="small" style={{ marginTop: '16px' }}>
                            <Descriptions.Item label="Invoice Number">{invoiceData.invoiceNumber}</Descriptions.Item>
                            <Descriptions.Item label="Client">{invoiceData.clientName}</Descriptions.Item>
                            <Descriptions.Item label="Total Amount">{formatIndianCurrency(invoiceData.totals.total)}</Descriptions.Item>
                            <Descriptions.Item label="Number of Items">{invoiceData.items.length}</Descriptions.Item>
                            <Descriptions.Item label="Financial Year">{invoiceData.financialYear}</Descriptions.Item>
                        </Descriptions>
                        <div style={{ marginTop: '16px', color: '#666', fontSize: '12px' }}>
                            * This action will mark the quotation as <strong>Invoiced</strong> and create a new invoice in the system.
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}

export default QuoteDetails;
